import { MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";

import { SUPPORT_PHONE } from "@/lib/constants";

/**
 * MACHINE HEALTH — single source of truth for site/field status indicators.
 *
 * Anywhere we render a machine-health dot in the app, the state, color,
 * label, and tooltip must come from this module. If the spec changes
 * (new state, new label, different threshold, etc.) edit this file only.
 *
 * --- The four states ---
 *
 *   green  "Healthy"        Fresh ping (< STALE_PING_THRESHOLD_S) AND
 *                           both subsystems (`watching`, `display_service`)
 *                           reporting true.
 *
 *   orange "Maintenance"    `field.status === "inactive"` — an admin has
 *                           deliberately taken the field offline.
 *
 *   grey   "Missing Status" `latestPing` is null. We have never seen a
 *                           status from this machine — meaningfully
 *                           different from "broken" because we don't
 *                           know whether the machine is still coming
 *                           online for the first time. Tooltip directs
 *                           the user to contact support.
 *
 *   red    "Error"          Either the last ping is older than the stale
 *                           threshold (machine has stopped phoning home),
 *                           or a subsystem flag on a fresh ping is false
 *                           (`watching === false` or
 *                           `display_service === false`). Both mean
 *                           "something is wrong, contact support" — the
 *                           UX collapses them to one red Error state.
 *
 * --- Precedence (top-to-bottom; first match wins) ---
 *
 *   1. latestPing == null → grey   ("Missing Status")
 *   2. Stale ping          → red    ("Error")
 *   3. status=inactive     → orange ("Maintenance")
 *   4. Subsystem bad       → red    ("Error")
 *   5. otherwise           → green  ("Healthy")
 *
 * Null-ping and stale-ping both run before the `inactive` check because
 * if we can't reach the machine we can't trust the admin-set `inactive`
 * flag has been honored — that flag may be stale itself. Confirmed in
 * Slack: missing/stale data invalidates trust in the inactive signal.
 *
 * `status === "inactive"` is checked before the subsystem flags so an
 * admin-disabled field with a fresh ping reads "Maintenance" regardless
 * of what its subsystems happen to report on the way down. The intent
 * ("we took this offline on purpose") is the more useful message than
 * "a subsystem is failing" on a field nobody is supposed to be using.
 *
 * --- Aggregation across multiple fields ---
 *
 * `getWorstHealth` collapses an array of fields to a single worst state
 * for the complex-level "Needs Attention" badge. Ranking, worst to best:
 *
 *   red > orange > grey > green
 *
 * Rationale: red is an active fault and needs intervention immediately.
 * orange is a deliberate admin state (less urgent than a fault). grey is
 * an unknown — worth surfacing, but not above a known fault or a known
 * maintenance state. green is no-op.
 *
 * --- Callers with only a boolean signal ---
 *
 * Some indicator call sites (SmartPark automation modal, event-form LED
 * health) only know "subsystem ok? yes/no" — they don't have latestPing
 * or field.status. Those callers go through `subsystemHealth(boolean)`
 * so the label and color decisions stay in this file. They get green
 * "Healthy" or red "Error".
 */

export type MachineHealthState = "green" | "orange" | "grey" | "red";

export type MachineHealth = {
  state: MachineHealthState;
  label: string;
  tooltip: string | null;
};

// 17 minutes. Tuned to "longer than the longest expected ping gap under
// healthy conditions" — anything beyond this and the data is stale enough
// that we'd rather say "Missing Status" than risk reporting wrong info.
const STALE_PING_THRESHOLD_S = 17 * 60;

export const CONTACT_SUPPORT_TOOLTIP = `This machine is currently unavailable. Please contact support at ${SUPPORT_PHONE}`;

// Canonical MachineHealth values. Every branch of getMachineHealth and
// every public helper returns one of these — never a freshly-constructed
// object — so the label/tooltip text exists in exactly one place.
const HEALTHY: MachineHealth = {
  state: "green",
  label: "Healthy",
  tooltip: null,
};
const MAINTENANCE: MachineHealth = {
  state: "orange",
  label: "Maintenance",
  tooltip: CONTACT_SUPPORT_TOOLTIP,
};
const MISSING_STATUS: MachineHealth = {
  state: "grey",
  label: "Missing Status",
  tooltip: "This machine may not support health status",
};
const SUBSYSTEM_ERROR: MachineHealth = {
  state: "red",
  label: "Error",
  tooltip: CONTACT_SUPPORT_TOOLTIP,
};

// Single source of truth for dot color per state. Imported by every
// component that renders a health dot (HealthyIndicator, ComplexHealth).
// To recolor a state app-wide, edit this map.
export const MACHINE_HEALTH_DOT_CLASS: Record<MachineHealthState, string> = {
  green: "bg-green-500",
  orange: "bg-orange-500",
  grey: "bg-gray-400",
  red: "bg-red-500",
};

export function getMachineHealth(
  field: Pick<MachinePublicHydrated, "status" | "latestPing">,
  // Compare ping freshness against the data snapshot, not local clock, to
  // avoid false negatives when the user returns to a stale tab that hasn't
  // refetched yet. Callers pass the underlying query's `dataUpdatedAt`.
  asOfMs: number = Date.now(),
): MachineHealth {
  const pingTs = field.latestPing?.data?.status?.now;

  // 1. We have never seen a status from this machine. Distinct from
  //    "broken" — could be a brand-new install still coming online.
  if (pingTs == null) return MISSING_STATUS;

  // 2. We had a ping at some point, but it's old. Treat as error: the
  //    machine has stopped phoning home and we can't trust any of the
  //    flags from that stale data.
  const nowS = asOfMs / 1000;
  if (nowS - pingTs > STALE_PING_THRESHOLD_S) return SUBSYSTEM_ERROR;

  // 3. Deliberate admin offline. Checked before the subsystem flags so a
  //    field that was set inactive still reads "Maintenance" even if its
  //    subsystems happen to report failure on the way down.
  if (field.status === "inactive") return MAINTENANCE;

  // 4. Subsystem failure on a fresh ping → real, observed fault.
  //    Either flag being false is a hard failure that requires support.
  //    The user sees one red "Error" state; the breakdown below is for
  //    engineers / support digging into the data:
  //      watching=true,  display=false → LED board not functional; data
  //                                      collection may still be occurring
  //      watching=false, display=true  → balltracker not watching (could
  //                                      be the boot warmup window, or a
  //                                      real fault that persists past boot)
  //      watching=false, display=false → overall failure of data collection
  //                                      and LED board display
  const watching = field.latestPing?.data?.status?.watching;
  const displayService = field.latestPing?.data?.status?.display_service;
  if (watching === false || displayService === false) return SUBSYSTEM_ERROR;

  // 5. Fresh ping, not inactive, both subsystems good.
  return HEALTHY;
}

/**
 * Boolean-input convenience for callers that only have a single
 * subsystem signal (e.g. SmartPark `watching` flag, event-form
 * `isFieldHealthy` boolean) and no full field data. Routes them
 * through the same canonical Healthy / Error constants so the
 * label and color decisions still live here.
 */
export function subsystemHealth(isOk: boolean): MachineHealth {
  return isOk ? HEALTHY : SUBSYSTEM_ERROR;
}

// Worst-to-best ordering for getWorstHealth. See header doc for rationale.
// Edit here to change how the complex-level badge prioritizes states.
const STATE_RANK: Record<MachineHealthState, number> = {
  green: 0,
  grey: 1,
  orange: 2,
  red: 3,
};

export function getWorstHealth(
  fields: Array<Pick<MachinePublicHydrated, "status" | "latestPing">>,
  asOfMs: number = Date.now(),
): MachineHealthState {
  return fields.reduce<MachineHealthState>((worst, field) => {
    const state = getMachineHealth(field, asOfMs).state;
    return STATE_RANK[state] > STATE_RANK[worst] ? state : worst;
  }, "green");
}
